import { Renderer } from './vendor/ogl/src/core/Renderer.js';
import { Program } from './vendor/ogl/src/core/Program.js';
import { Mesh } from './vendor/ogl/src/core/Mesh.js';
import { Triangle } from './vendor/ogl/src/extras/Triangle.js';
import { Vec3 } from './vendor/ogl/src/math/Vec3.js';
import { t } from './i18n.mjs?v=20260806.100';

// The orb turns at one slow rate, always. Fast enough to be alive, slow enough
// that nothing about it competes with the two fields in front of it.
const LOGIN_ORB_IDLE_SPIN = .06;

const VERTEX_SHADER = `
precision highp float;
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main(){vUv=uv;gl_Position=vec4(position,0.0,1.0);}`;

const FRAGMENT_SHADER = `
precision highp float;
uniform float iTime;
uniform vec3 iResolution;
uniform float hue;
uniform float hover;
uniform float rot;
uniform float hoverIntensity;
uniform vec3 backgroundColor;
varying vec2 vUv;

vec3 rgb2yiq(vec3 c){
  float y=dot(c,vec3(0.299,0.587,0.114));
  float i=dot(c,vec3(0.596,-0.274,-0.322));
  float q=dot(c,vec3(0.211,-0.523,0.312));
  return vec3(y,i,q);
}
vec3 yiq2rgb(vec3 c){
  return vec3(
    c.x+0.956*c.y+0.621*c.z,
    c.x-0.272*c.y-0.647*c.z,
    c.x-1.106*c.y+1.703*c.z
  );
}
vec3 adjustHue(vec3 color,float hueDeg){
  float hueRad=hueDeg*3.14159265/180.0;
  vec3 yiq=rgb2yiq(color);
  float cosA=cos(hueRad);float sinA=sin(hueRad);
  float i=yiq.y*cosA-yiq.z*sinA;
  float q=yiq.y*sinA+yiq.z*cosA;
  yiq.y=i;yiq.z=q;
  return yiq2rgb(yiq);
}
vec3 hash33(vec3 p3){
  p3=fract(p3*vec3(0.1031,0.11369,0.13787));
  p3+=dot(p3,p3.yxz+19.19);
  return -1.0+2.0*fract(vec3(p3.x+p3.y,p3.x+p3.z,p3.y+p3.z)*p3.zyx);
}
float snoise3(vec3 p){
  const float K1=0.333333333;const float K2=0.166666667;
  vec3 i=floor(p+(p.x+p.y+p.z)*K1);
  vec3 d0=p-(i-(i.x+i.y+i.z)*K2);
  vec3 e=step(vec3(0.0),d0-d0.yzx);
  vec3 i1=e*(1.0-e.zxy);vec3 i2=1.0-e.zxy*(1.0-e);
  vec3 d1=d0-(i1-K2);vec3 d2=d0-(i2-K1);vec3 d3=d0-0.5;
  vec4 h=max(0.6-vec4(dot(d0,d0),dot(d1,d1),dot(d2,d2),dot(d3,d3)),0.0);
  vec4 n=h*h*h*h*vec4(dot(d0,hash33(i)),dot(d1,hash33(i+i1)),dot(d2,hash33(i+i2)),dot(d3,hash33(i+1.0)));
  return dot(vec4(31.316),n);
}
vec4 extractAlpha(vec3 colorIn){
  float a=max(max(colorIn.r,colorIn.g),colorIn.b);
  return vec4(colorIn.rgb/(a+1e-5),a);
}
const vec3 baseColor1 = vec3(0.611765, 0.262745, 0.996078);
const vec3 baseColor2 = vec3(0.298039, 0.760784, 0.913725);
const vec3 baseColor3 = vec3(0.062745, 0.078431, 0.600000);
const float innerRadius=0.6;
const float noiseScale=0.65;
float light1(float intensity,float attenuation,float dist){return intensity/(1.0+dist*attenuation);}
float light2(float intensity,float attenuation,float dist){return intensity/(1.0+dist*dist*attenuation);}
vec4 draw(vec2 uv){
  vec3 color1=adjustHue(baseColor1,hue);
  vec3 color2=adjustHue(baseColor2,hue);
  vec3 color3=adjustHue(baseColor3,hue);
  float ang=atan(uv.y,uv.x);float len=length(uv);float invLen=len>0.0?1.0/len:0.0;
  float bgLuminance=dot(backgroundColor,vec3(0.299,0.587,0.114));
  float n0=snoise3(vec3(uv*noiseScale,iTime*0.5))*0.5+0.5;
  float r0=mix(mix(innerRadius,1.0,0.4),mix(innerRadius,1.0,0.6),n0);
  float d0=distance(uv,(r0*invLen)*uv);
  float v0=light1(1.0,10.0,d0);
  v0*=smoothstep(r0*1.05,r0,len);
  float innerFade=smoothstep(r0*0.8,r0*0.95,len);
  v0*=mix(innerFade,1.0,bgLuminance*0.7);
  float cl=cos(ang+iTime*2.0)*0.5+0.5;
  float a=iTime*-1.0;vec2 pos=vec2(cos(a),sin(a))*r0;
  float d=distance(uv,pos);float v1=light2(1.5,5.0,d);v1*=light1(1.0,50.0,d0);
  float v2=smoothstep(1.0,mix(innerRadius,1.0,n0*0.5),len);
  float v3=smoothstep(innerRadius,mix(innerRadius,1.0,0.5),len);
  vec3 colBase=mix(color1,color2,cl);float fadeAmount=mix(1.0,0.1,bgLuminance);
  vec3 darkCol=mix(color3,colBase,v0);darkCol=(darkCol+v1)*v2*v3;darkCol=clamp(darkCol,0.0,1.0);
  vec3 lightCol=(colBase+v1)*mix(1.0,v2*v3,fadeAmount);lightCol=mix(backgroundColor,lightCol,v0);lightCol=clamp(lightCol,0.0,1.0);
  return extractAlpha(mix(darkCol,lightCol,bgLuminance));
}
vec4 mainImage(vec2 fragCoord){
  vec2 center=iResolution.xy*0.5;float size=min(iResolution.x,iResolution.y);
  vec2 uv=(fragCoord-center)/size*2.0;
  float s=sin(rot);float c=cos(rot);uv=vec2(c*uv.x-s*uv.y,s*uv.x+c*uv.y);
  uv.x+=hover*hoverIntensity*0.1*sin(uv.y*10.0+iTime);
  uv.y+=hover*hoverIntensity*0.1*sin(uv.x*10.0+iTime);
  return draw(uv);
}
void main(){
  vec4 col=mainImage(vUv*iResolution.xy);
  vec3 color=col.rgb;float alpha=col.a;
  vec2 uv=((vUv*iResolution.xy)-(iResolution.xy*0.5))/min(iResolution.x,iResolution.y)*2.0;
  float canvasRadius=length(uv);
  float edgeMask=1.0-smoothstep(0.80,0.96,canvasRadius);
  color=mix(backgroundColor,color,edgeMask);
  gl_FragColor=vec4(color,1.0);
}`;

const LOGIN_ORB_TYPING_PLAN = Object.freeze({
  hoverIntensity: 0.36,
  settleDelay: 460,
  pointerReactive: false,
});

export function loginOrbTypingPlan() {
  return { ...LOGIN_ORB_TYPING_PLAN };
}

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function splitTextMarkup(text) {
  return [...text].map((character) => character === ' '
    ? '<span class="login-orb-title__space">&nbsp;</span>'
    : `<span>${escapeHtml(character)}</span>`).join('');
}

export function emailSyntaxState(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return 'empty';
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized) ? 'ready' : 'invalid';
}

export function loginFailureKey(error) {
  const code = typeof error?.code === 'string' ? error.code : '';
  if (code === 'auth/user-disabled') return 'login.accountDisabled';
  if (code === 'auth/too-many-requests') return 'login.tooManyAttempts';
  if (['auth/network-request-failed', 'auth/timeout'].includes(code)) return 'login.networkError';
  if ([
    'auth/invalid-credential',
    'auth/invalid-email',
    'auth/user-not-found',
    'auth/wrong-password',
  ].includes(code)) return 'login.invalidCredentials';
  return 'login.serviceError';
}

export function loginTransitionPlan(reducedMotion = false) {
  return reducedMotion
    ? { expandDuration: 0.16, enterDuration: 0.18, coverOverscan: 1.2, blur: 0 }
    : { expandDuration: 0.72, enterDuration: 0.68, coverOverscan: 1.45, blur: 10 };
}

export function loginOrbMarkup({ locale } = {}) {
  const welcomeBack = t('login.welcomeBack', {}, locale);
  const accountEmail = t('login.accountEmail', {}, locale);
  const password = t('login.password', {}, locale);
  const showPassword = t('login.showPassword', {}, locale);
  const continueLabel = t('login.continue', {}, locale);
  return `<main class="login-orb-page" data-login-orb-page>
    <div class="login-orb-canvas" data-login-orb aria-hidden="true"></div>
    <section class="login-orb-content" aria-label="AY TMS sign in">
      <header class="login-orb-brand" aria-label="AY TMS">
        <img class="login-orb-wordmark" src="./assets/ay-tms-logo.svg?v=20260806.100-2" alt="AY TMS" width="560" height="180">
      </header>
      <h1 class="login-orb-title" aria-label="${escapeHtml(welcomeBack)}">${splitTextMarkup(welcomeBack)}</h1>
      <form class="login-orb-form" id="loginForm" data-login-ready="false" novalidate>
        <label class="login-orb-field">
          <span class="sr-only">${escapeHtml(accountEmail)}</span>
          <input id="email" data-login-input type="email" autocomplete="email" placeholder="${escapeHtml(accountEmail)}" required>
          <i class="login-orb-field-state" data-login-field-state="email" aria-hidden="true"></i>
        </label>
        <div class="login-orb-password-row">
          <label class="login-orb-field login-orb-password-field">
            <span class="sr-only">${escapeHtml(password)}</span>
            <input id="password" data-login-input type="password" autocomplete="current-password" placeholder="${escapeHtml(password)}" required>
            <i class="login-orb-field-state" data-login-field-state="password" aria-hidden="true"></i>
            <button class="login-orb-password-toggle" type="button" aria-label="${escapeHtml(showPassword)}" data-login-password-toggle>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></svg>
            </button>
          </label>
          <button class="login-orb-submit" type="submit" aria-label="${escapeHtml(continueLabel)}">
            <svg class="login-orb-truck" viewBox="0 0 64 38" aria-hidden="true">
              <path class="login-orb-truck__cargo" d="M4 7.5h33v21H4z"/>
              <path class="login-orb-truck__cab" d="M37 14h12l8 8v6.5H37z"/>
              <path class="login-orb-truck__window" d="M41 17h6.5l4.5 5h-11z"/>
              <path class="login-orb-truck__road" d="M2 33h59"/>
              <circle cx="15" cy="29.5" r="5"/><circle cx="47" cy="29.5" r="5"/>
              <circle class="login-orb-truck__hub" cx="15" cy="29.5" r="2"/><circle class="login-orb-truck__hub" cx="47" cy="29.5" r="2"/>
              <circle class="login-orb-truck__lamp" cx="57" cy="25" r="1.7"/>
            </svg>
          </button>
        </div>
        <div class="error login-error" id="loginError" role="alert" aria-live="assertive"></div>
      </form>
    </section>
  </main>`;
}

function prefersReducedMotion() {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
    && globalThis.document?.documentElement?.dataset?.motion !== 'on';
}

export function mountLoginOrb({ root, gsapRef = globalThis.gsap } = {}) {
  if (!root?.querySelector) return () => {};
  const container = root.querySelector('[data-login-orb]');
  const titleCharacters = [...root.querySelectorAll('.login-orb-title > span')];
  const brandElements = [...root.querySelectorAll('.login-orb-brand > *')];
  const formElements = [...root.querySelectorAll('.login-orb-form > *')];
  let renderer;
  let frame = 0;
  let resizeObserver;
  let destroyed = false;
  if (!prefersReducedMotion() && container) {
    try {
      renderer = new Renderer({ alpha: true, premultipliedAlpha: false, dpr: Math.min(2, globalThis.devicePixelRatio || 1) });
      const gl = renderer.gl;
      gl.clearColor(0, 0, 0, 0);
      container.append(gl.canvas);
      const program = new Program(gl, {
        vertex: VERTEX_SHADER,
        fragment: FRAGMENT_SHADER,
        uniforms: {
          iTime: { value: 0 },
          iResolution: { value: new Vec3(1, 1, 1) },
          hue: { value: 0 },
          hover: { value: 0 },
          rot: { value: 0 },
          hoverIntensity: { value: LOGIN_ORB_TYPING_PLAN.hoverIntensity },
          backgroundColor: { value: hexToVec3('#ffffff') },
        },
      });
      const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });
      const resize = () => {
        const width = Math.max(1, container.clientWidth);
        const height = Math.max(1, container.clientHeight);
        renderer.setSize(width, height);
        program.uniforms.iResolution.value.set(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height);
      };
      resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
      resizeObserver?.observe(container);
      resize();
      // The orb no longer reacts to typing (Alan, 2026-08-08). Every keystroke
      // used to drive `hover` to 1 and spin it up for as long as you kept typing,
      // so entering an email set the whole background moving behind the field you
      // were reading. It renders its own slow drift and nothing else.
      let lastTime = 0;
      let currentRot = 0;
      const render = (time) => {
        if (destroyed) return;
        const delta = lastTime ? Math.min((time - lastTime) * .001, .05) : 0;
        lastTime = time;
        program.uniforms.iTime.value = time * .001;
        // A constant, unhurried rotation. It was `delta * .3 * hover`, and hover
        // was only ever non-zero while somebody was typing.
        currentRot += delta * LOGIN_ORB_IDLE_SPIN;
        program.uniforms.rot.value = currentRot;
        renderer.render({ scene: mesh });
        frame = requestAnimationFrame(render);
      };
      frame = requestAnimationFrame(render);
      container._loginOrbCleanup = () => {
        resizeObserver?.disconnect();
        // Shrink and detach the drawing surface after the final frame. Calling
        // WEBGL_lose_context synchronously can deadlock software-rendered
        // browsers while accessibility/trace tooling is reading the canvas.
        gl.canvas.width = 1;
        gl.canvas.height = 1;
        gl.canvas.remove();
      };
    } catch (_) {
      container?.classList.add('login-orb-canvas--fallback');
    }
  } else {
    container?.classList.add('login-orb-canvas--fallback');
  }
  if (gsapRef && titleCharacters.length && !prefersReducedMotion()) {
    gsapRef.fromTo(brandElements, { opacity: 0, y: -14, filter: 'blur(5px)' }, {
      opacity: 1, y: 0, filter: 'blur(0px)', duration: .92, stagger: .09, ease: 'power3.out',
    });
    gsapRef.fromTo(titleCharacters, { opacity: 0, y: 30, filter: 'blur(7px)' }, {
      opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.05, stagger: .045, ease: 'power3.out',
    });
    gsapRef.fromTo(formElements, { opacity: 0, y: 18, filter: 'blur(5px)' }, {
      opacity: 1, y: 0, filter: 'blur(0px)', duration: .94, stagger: .11, delay: .24, ease: 'power3.out',
    });
  }
  return () => {
    destroyed = true;
    cancelAnimationFrame(frame);
    container?._loginOrbCleanup?.();
  };
}

function hexToVec3(color) {
  const normalized = String(color || '').trim();
  const match = /^#([0-9a-f]{6})$/i.exec(normalized);
  if (!match) return new Vec3(1, 1, 1);
  const value = Number.parseInt(match[1], 16);
  return new Vec3(
    ((value >> 16) & 255) / 255,
    ((value >> 8) & 255) / 255,
    (value & 255) / 255,
  );
}

export function setLoginFieldState(root, field, state) {
  const host = root?.querySelector?.(`[data-login-field-state="${field}"]`)?.closest?.('.login-orb-field');
  if (!host) return;
  host.dataset.state = state;
}

export function runLoginSuccessExit(page, {
  gsapRef = globalThis.gsap,
  fallbackMs,
} = {}) {
  if (!page) return Promise.resolve();
  const plan = loginTransitionPlan(prefersReducedMotion());
  const orb = page.querySelector('[data-login-orb]');
  const chrome = [...page.querySelectorAll('.login-orb-brand, .login-orb-content')];
  const rect = orb?.getBoundingClientRect?.() ?? { width: 1, height: 1 };
  const viewportWidth = Math.max(1, globalThis.innerWidth || page.clientWidth || 1);
  const viewportHeight = Math.max(1, globalThis.innerHeight || page.clientHeight || 1);
  const coverScale = Math.max(
    viewportWidth / Math.max(1, rect.width),
    viewportHeight / Math.max(1, rect.height),
  ) * plan.coverOverscan;
  page.dataset.loginTransitionState = 'orb-expanding';
  if (!gsapRef) {
    chrome.forEach((element) => { element.style.opacity = '0'; });
    if (orb) orb.style.transform = `translate(-50%, -50%) scale(${coverScale})`;
    return new Promise((resolve) => setTimeout(resolve, plan.expandDuration * 1000));
  }
  return new Promise((resolve) => {
    let settled = false;
    let timeline;
    let fallbackTimer;
    const maximumWait = Number.isFinite(fallbackMs)
      ? Math.max(0, fallbackMs)
      : Math.max(1400, (plan.expandDuration * 1000) + 650);
    const finish = ({ killTimeline = false } = {}) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout?.(fallbackTimer);
      if (killTimeline) timeline?.kill?.();
      resolve();
    };

    // Authentication must never be held hostage by a throttled or interrupted
    // animation frame. The normal GSAP completion wins; this guard only takes
    // over if the timeline stalls (for example in a backgrounded browser tab).
    fallbackTimer = globalThis.setTimeout?.(
      () => finish({ killTimeline: true }),
      maximumWait,
    );

    try {
      timeline = gsapRef.timeline({ onComplete: finish });
      timeline.to(chrome, {
        opacity: 0,
        scale: .97,
        filter: `blur(${Math.min(7, plan.blur)}px)`,
        duration: Math.min(.3, plan.expandDuration * .52),
        stagger: .025,
        ease: 'power2.in',
      }, 0);
      if (orb) {
        timeline.to(orb, {
          scale: coverScale,
          opacity: 1,
          filter: 'saturate(1.04) contrast(1.015)',
          duration: plan.expandDuration,
          ease: 'power3.inOut',
        }, 0);
      }
      timeline.to(page, {
        backgroundColor: '#ffffff',
        duration: plan.expandDuration,
        ease: 'power2.inOut',
      }, 0);
    } catch (_) {
      finish({ killTimeline: true });
    }
  });
}

export function runDashboardLoginEntrance(root, { gsapRef = globalThis.gsap } = {}) {
  if (!root) return;
  const plan = loginTransitionPlan(prefersReducedMotion());
  const documentRef = root.ownerDocument;
  const entranceHost = root.querySelector('.page') ?? root;
  entranceHost.dataset.pageEntranceState = 'running';
  const wash = documentRef?.createElement?.('div');
  if (wash) {
    wash.className = 'login-orb-dashboard-wash';
    wash.setAttribute('aria-hidden', 'true');
    root.append(wash);
  }
  const candidates = [
    root.querySelector('.topbar'),
    ...root.querySelectorAll('.dashboard-metrics > *, .dashboard-secondary-data, .floating-work-queue-host'),
  ].filter(Boolean);
  const fixedViewportCandidates = [
    root.querySelector('.container-operations-panel'),
  ].filter(Boolean);
  if (!gsapRef) {
    wash?.remove();
    entranceHost.dataset.pageEntranceState = 'complete';
    return;
  }
  const timeline = gsapRef.timeline({
    onComplete: () => {
      wash?.remove();
      entranceHost.dataset.pageEntranceState = 'complete';
    },
  });
  if (wash) {
    timeline.fromTo(wash, {
      opacity: 1,
      scale: 1,
      filter: 'saturate(1.28) blur(0px)',
    }, {
      opacity: 0,
      scale: 1.08,
      filter: `saturate(1.08) blur(${plan.blur}px)`,
      duration: plan.enterDuration,
      ease: 'power2.out',
    }, 0);
  }
  timeline.fromTo(candidates, {
    opacity: 0,
    y: 26,
    scale: .985,
    filter: `blur(${Math.min(8, plan.blur)}px)`,
  }, {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    duration: plan.enterDuration,
    stagger: .055,
    ease: 'power3.out',
    clearProps: 'transform,opacity,filter',
  }, .08);
  timeline.fromTo(fixedViewportCandidates, {
    opacity: 0,
  }, {
    opacity: 1,
    duration: plan.enterDuration,
    ease: 'power3.out',
    clearProps: 'opacity',
  }, .12);
}
